import { Product, UnboundProductData, UnboundProductOptionData } from "@/products/product.base.js";
import { VlaicuProduct } from "@/products/product.vlaicu.js";
import { STORE_LOCALE } from "@/store.constants";
import type { Locale, ProductSelector } from "@/store.js";
import { FetchedProduct, INGNORE_CAMPAIGN, Provider, ProviderData } from "./provider.base.js";

export interface FetchResponse {
  code               : number;
  message            : string;
  campaign           : string;
  campaignType       : string;
  platformProductId  : string;
  buyLinkBaseUrl?    : string;
  verifoneProductCode: string;
  product            : ProductData;
}

export interface ProductData {
  productId      : string;
  productName    : string;
  buyLinkBaseUrl?: string;
  options        : OptionData[];
}

export interface OptionData {
  slots                 : number;
  months                : number;
  currency              : string;
  price                 : number;
  discountedPrice       : number;
  discountAmount        : number;
  discountPercentage    : number;
  buyLink               : string;
  verifoneOptionCode?   : string;
  verifoneProductId?    : string;
  coupon?               : string;
  checkoutPaymentPeriod?: string;
  optionCode?           : string
}
export class VlaicuProvider extends Provider {
  constructor(param: ProviderData) {
    super(param)
  }

  protected _getProduct(product: UnboundProductData | undefined): Product | undefined {
    return product ? new VlaicuProduct({ ...product, store: this.store }) : product;
  }

  async fetch({ id, campaign }: ProductSelector): Promise<FetchedProduct | undefined> {
    const adaptedId = (await this.adaptTo({ id })).id;
    const computedCampaign = await this.getCampaign(adaptedId, campaign)
    const apiURL = this.buildApiURL(adaptedId, this.store[STORE_LOCALE], computedCampaign);

    let fetchResponse: FetchResponse;
    try {
      const response = await fetch(apiURL.href, {
        method : "get",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) return undefined;

      fetchResponse = await response.json();
    } catch (error) {
      console.error("Failed to fetch product:", error);
      return undefined;
    }

    // Process variations into option data.
    const { options, currency } = await this.processVariations(
      adaptedId,
      fetchResponse.product.options
    );

    // Return an UnboundProductData.
    return {
      overrides: {
        oldCampaign: campaign,
        newCampaign: computedCampaign
      },
      product: {
        id            : adaptedId,
        alias         : id,
        name          : fetchResponse.product.productName,
        campaign      : computedCampaign || fetchResponse.campaign,
        campaignType  : fetchResponse.campaignType || '',
        buyLinkBaseUrl: fetchResponse.buyLinkBaseUrl,
        currency,
        platformId    : fetchResponse.platformProductId || fetchResponse.product.productId,
        options
      }
    }
  }

  private buildApiURL(id: string, locale: Locale, campaign?: string): URL {
    const apiParams = ["products", id, "locale", locale];
    if (campaign) {
      apiParams.push("campaign", INGNORE_CAMPAIGN.includes(String(campaign)) ? "null" : campaign);
    }
    const url = new URL(`https://www.bitdefender.com/p-api/v1/${apiParams.join("/")}`);
    return url;
  }

  private async processVariations(
    id: string,
    variations: OptionData[]
  ): Promise<{ options: Map<string, UnboundProductOptionData>; currency: string }> {
    const options = new Map<string, UnboundProductOptionData>();
    let currency = "";

    // Loop through devices and subscriptions.
    for (const variation of variations) {
      const variationData: UnboundProductOptionData = {
        devices              : variation.slots,
        subscription         : variation.months,
        price                : Number(variation.price),
        discountedPrice      : Number(variation.price === variation.discountedPrice ? NaN : variation.discountedPrice),
        buyLink              : variation.buyLink,
        verifoneOptionCode   : variation.verifoneOptionCode,
        verifoneProductId    : variation.verifoneProductId,
        coupon               : variation.coupon,
        checkoutPaymentPeriod: variation.checkoutPaymentPeriod,
        bundle               : []
      };

      // Capture common info.
      currency = variation.currency;

      options.set(`${variation.slots}-${variation.months}`, variationData);
    }
    return { options, currency };
  }
}
